/**
 * Utility functions for calculating spots based on billboard operating hours
 */

/**
 * Parse time string in HH:MM or HH:MM:SS format to hours (decimal)
 * @param timeString - Time in format "HH:MM" or "HH:MM:SS"
 * @returns Hours as decimal number (e.g., "07:30" -> 7.5)
 */
function parseTimeToHours(timeString: string): number | null {
  if (!timeString) return null;
  
  // Strip seconds if present and split into parts
  const timeParts = timeString.split(':');
  if (timeParts.length < 2) return null;
  
  const hours = parseInt(timeParts[0], 10);
  const minutes = parseInt(timeParts[1], 10);
  
  if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }
  
  return hours + (minutes / 60);
}

/**
 * Calculate total running hours for a billboard display
 * Handles midnight crossover (e.g., 07:00 to 01:00 = 18 hours)
 * 
 * Formula: If displayOffTime < displayOnTime, treat as next day
 * Total hours = (24:00 - displayOnTime) + displayOffTime
 * 
 * @param displayOnTime - Display start time in HH:MM or HH:MM:SS format
 * @param displayOffTime - Display end time in HH:MM or HH:MM:SS format
 * @returns Total running hours, or null if invalid input
 */
export function calculateTotalRunningHours(
  displayOnTime: string,
  displayOffTime: string
): number | null {
  const onTimeHours = parseTimeToHours(displayOnTime);
  const offTimeHours = parseTimeToHours(displayOffTime);
  
  if (onTimeHours === null || offTimeHours === null) {
    return null;
  }
  
  // If off time is less than on time, it crosses midnight
  if (offTimeHours < onTimeHours) {
    // (24 - onTime) + offTime
    return (24 - onTimeHours) + offTimeHours;
  }
  
  // Normal case: same day
  return offTimeHours - onTimeHours;
}

/**
 * Calculate spots per day for a billboard
 * 
 * Formula: spotsPerDay = totalRunningHours * spotsPerHour
 * 
 * @param spotsPerHour - Number of spots played per hour
 * @param displayOnTime - Display start time in HH:MM or HH:MM:SS format
 * @param displayOffTime - Display end time in HH:MM or HH:MM:SS format
 * @param screensCount - Number of screens/panels (default 1)
 * @returns Calculated spots per day, or null if invalid input
 */
export function calculateSpotsPerDay(
  spotsPerHour: number,
  displayOnTime: string,
  displayOffTime: string,
  screensCount: number = 1
): number | null {
  if (!spotsPerHour || spotsPerHour <= 0) {
    return null;
  }
  
  const runningHours = calculateTotalRunningHours(displayOnTime, displayOffTime);
  
  if (runningHours === null || runningHours <= 0) {
    return null;
  }
  
  // Calculate spots: running hours * spots per hour * number of screens
  return Math.round(runningHours * spotsPerHour * screensCount);
}
